/* Серверный источник шаблонного «Профиля»: движок рисует те же карточку, вкладки и листы, данные берёт отсюда. */
import { endpoints } from '../../api';
import type { ProfileResponse } from '../../api/account';
import type { PlanCatalog } from '../../api/commerce';
import type { HistoryOrder, HistoryPage } from '../../api/history';
import type { Balance, Me } from '../../api/types';
import { peopleServer, syncPeople } from './people-server';
import { historyServer, toItem } from '../history/history-server';

export interface ProfileSnapshot {
  me: Me;
  profile: ProfileResponse | null;
  balance: Balance | null;
  /** Сколько своих голосов даёт подписка (наибольший voices_limit тарифов GET /app/plans); null — тарифы не загрузились */
  planVoices: number | null;
}

export interface ProfileWeek {
  from: number;
  count: number;
}

export interface FavoriteWork {
  id: number;
  kind: string;
  title: string;
  text: string;
  at: number;
  fav: boolean;
  vid: number | null;
  voiceName?: string;
  characterName?: string;
  /** Заказ сервера: по нему грузится превью результата (миниатюра строки, как в «Истории») */
  order?: HistoryOrder;
}

function publish(me: Me) {
  window.BotServerMe = me;
  document.dispatchEvent(new CustomEvent('steos:account-updated', { detail: me }));
}

/** Сервер отдаёт недели массивом {from, count}; всё остальное считается отсутствием данных */
function weeks(profile: ProfileResponse | null): ProfileWeek[] {
  if (!profile?.capabilities.weeks || !Array.isArray(profile.weeks)) return [];
  return (profile.weeks as Array<{ from?: unknown; count?: unknown }>)
    .map(week => ({ from: Date.parse(String(week.from)), count: Number(week.count) }))
    .filter(week => Number.isFinite(week.from) && Number.isFinite(week.count));
}

/** Предел своих голосов с подпиской — наибольший у тарифов; тарифов нет — неизвестен */
export function planVoices(plans: PlanCatalog | null): number | null {
  const limits = (plans?.items ?? []).map(plan => plan.voices_limit).filter(n => Number.isFinite(n) && n > 0);
  return limits.length ? Math.max(...limits) : null;
}

const favorite = (order: HistoryOrder): FavoriteWork => ({
  id: order.id,
  kind: toItem(order).kind,
  title: order.title ?? '',
  text: order.text ?? '',
  at: Date.parse(order.created_at),
  fav: order.fav !== false,
  vid: order.voice_id ?? null,
  voiceName: toItem(order).voiceName,
  characterName: toItem(order).characterName,
  order,
});

export const profileServer = {
  /** Профиль и баланс необязательны: без них карточка показывает остаток из Me, а статистика — пустой график */
  async load(): Promise<ProfileSnapshot> {
    const [me, profile, balance, plans] = await Promise.all([
      endpoints.me(),
      endpoints.account.profile().catch(() => null),
      endpoints.balance().catch(() => null),
      endpoints.commerce.plans().catch(() => null),
    ]);
    publish(me);
    return { me, profile, balance, planVoices: planVoices(plans) };
  },
  weeks,
  people: syncPeople,
  variant: { rename: peopleServer.renameVariant, drop: peopleServer.dropVariant },
  async telegramRefresh(): Promise<Me> {
    const me = await endpoints.account.telegramRefresh();
    publish(me);
    return me;
  },
  async favorites(): Promise<FavoriteWork[]> {
    const items = new Map<number, FavoriteWork>(),
      cursors = new Set<string>();
    let cursor: string | null = null;
    do {
      const page: HistoryPage = cursor
        ? await endpoints.history.list({ tab: 'fav', q: '', sort: 'new' }, cursor)
        : await endpoints.history.list({ tab: 'fav', q: '', sort: 'new' });
      page.items.forEach(order => items.set(order.id, favorite(order)));
      cursor = page.next_cursor;
      // A broken cursor must surface as an error rather than silently truncate the list.
      if (cursor && cursors.has(cursor)) throw new Error('favorites_cursor_repeated');
      if (cursor) cursors.add(cursor);
    } while (cursor);
    return [...items.values()];
  },
  /** Превью результата работы «Избранного» (то же, что миниатюра строки «Истории»): уже загруженное — сразу, иначе null */
  thumbNow: (work: FavoriteWork): string | null => (work.order ? (historyServer.thumbNow(work.order) ?? null) : null),
  /** Загрузить превью результата; у работы без картинки (озвучка, текст) — null */
  thumb: (work: FavoriteWork): Promise<string | null> => (work.order ? historyServer.thumb(work.order) : Promise.resolve(null)),
  async favorite(id: number, fav: boolean): Promise<void> {
    await endpoints.history.favorite(id, fav);
  },
};

export type ProfileServer = typeof profileServer;
